import type { Order } from '../../types/orders.types'
import { safeHttpUrl } from './safeHttpUrl'

/**
 * Fulfilment & tracking (PE-2167).
 *
 * Every field here was already coming back on `GET /orders/:id` and none of it was rendered — the
 * merchant could set a tracking number through "mark dispatched" and then had nowhere to read it
 * back. All of it is commonly null (nothing on development has shipped yet), so this renders an
 * honest "not set" rather than inventing placeholder shipping data.
 */

const TITLE_GRADIENT: React.CSSProperties = {
  lineHeight: '140%',
  letterSpacing: '0%',
  background:
    'linear-gradient(0deg, #3F3E42, #3F3E42), linear-gradient(0deg, rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.2)), linear-gradient(0deg, rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.2))',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text',
}

const formatDateTime = (iso: string | null) => {
  if (!iso) return null
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? null : d.toLocaleString()
}

/** Turn API SCREAMING_SNAKE / kebab values into something readable. */
const humanise = (v: string | null) =>
  v ? v.replace(/[_-]+/g, ' ').toLowerCase().replace(/^./, (c) => c.toUpperCase()) : null

function Field({ label, value, href }: { label: string; value: string | null; href?: string | null }) {
  const safeHref = safeHttpUrl(href)
  return (
    <div>
      <p className="text-[13px] text-muted-foreground">{label}</p>
      {value ? (
        safeHref ? (
          <a
            href={safeHref}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[15px] font-medium text-blue-600 hover:underline break-all"
          >
            {value}
          </a>
        ) : (
          <p className="text-[15px] font-medium text-foreground break-all">{value}</p>
        )
      ) : (
        <p className="text-[15px] text-muted-foreground">Not set</p>
      )}
    </div>
  )
}

export function FulfillmentCard({ order }: { order: Order }) {
  const f = order.fulfillment

  return (
    <div className="border border-[#B8B5B6] rounded-[25px] bg-card p-[20px]">
      <div className="flex items-center gap-[15px] mb-[20px]">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M1 3h15v13H1z" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M16 8h4l3 3v5h-7V8z" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          <circle cx="5.5" cy="18.5" r="2.5" stroke="#6B7280" strokeWidth="2" />
          <circle cx="18.5" cy="18.5" r="2.5" stroke="#6B7280" strokeWidth="2" />
        </svg>
        <h3 className="text-[19px] font-semibold" style={TITLE_GRADIENT}>
          Fulfilment &amp; Tracking
        </h3>
      </div>

      <div className="grid grid-cols-3 gap-[14px]">
        <Field label="Fulfilment status" value={humanise(f.status)} />
        <Field label="Carrier" value={f.carrier ?? humanise(f.provider)} />
        <Field label="Tracking number" value={f.trackingNumber} href={f.trackingUrl} />

        <Field label="Delivery method" value={humanise(f.deliveryMethod ?? f.shippingMethod)} />
        <Field label="Shipped" value={formatDateTime(f.shippedAt)} />
        <Field label="Delivered" value={formatDateTime(f.deliveredAt)} />
      </div>

      {(f.pickupAgent || f.selfArrangedInstruction || safeHttpUrl(f.labelUrl) || f.reference) && (
        <div className="grid grid-cols-3 gap-[14px] mt-[14px] pt-[14px] border-t border-border">
          {f.pickupAgent && <Field label="Pickup agent" value={f.pickupAgent} />}
          {f.reference && <Field label="Provider reference" value={f.reference} />}
          {safeHttpUrl(f.labelUrl) && <Field label="Shipping label" value="Open label" href={f.labelUrl} />}
          {f.selfArrangedInstruction && (
            <div className="col-span-3">
              <p className="text-[13px] text-muted-foreground">Self-arranged instruction</p>
              <p className="text-[15px] text-foreground">{f.selfArrangedInstruction}</p>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
